
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Loader2, ExternalLink } from "lucide-react";
import { usePaymentMethodJobs } from "@/hooks/useFinanceReport";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCompanyLocale } from "@/hooks/useCompanyLocale";

interface PaymentMethodDetailsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    paymentMethod: string | null;
    startDate: Date;
    endDate: Date;
    timezone: string;
}

export const PaymentMethodDetailsDialog = ({
    isOpen,
    onClose,
    title,
    paymentMethod,
    startDate,
    endDate,
    timezone,
}: PaymentMethodDetailsDialogProps) => {
    const navigate = useNavigate();
    const { data: jobs, isLoading } = usePaymentMethodJobs(
        startDate,
        endDate,
        timezone,
        paymentMethod
    );

    const { formatMoney: formatCurrency } = useCompanyLocale();

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{title} - Job Breakdown</DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-auto mt-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : !jobs?.length ? (
                        <div className="text-center p-8 text-muted-foreground">
                            No jobs found for this payment method in the selected period.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Job #</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {jobs.map((job) => (
                                    <TableRow key={job.id}>
                                        <TableCell>
                                            {job.date ? format(new Date(job.date), 'dd MMM yyyy') : '-'}
                                        </TableCell>
                                        <TableCell className="font-medium">{job.jobNumber}</TableCell>
                                        <TableCell>{job.customerName}</TableCell>
                                        <TableCell className="text-right">
                                            {formatCurrency(job.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => navigate(`/jobs/${job.jobId}`)}
                                                title="View Job"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
